import { Fragment, type JSX } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { SEGMENTOS_ORCAMENTO } from '@orc/shared';
import { CAMINHO_TROCA_SENHA, RotaProtegida } from './auth/RotaProtegida.js';
import { Vazio } from './componentes/Estados.js';
import { Layout } from './layout/Layout.js';
import { Catalogo } from './paginas/Catalogo.js';
import { Dashboard } from './paginas/Dashboard.js';
import { Importacao } from './paginas/Importacao.js';
import { Logs } from './paginas/Logs.js';
import { Login } from './paginas/Login.js';
import { OrcamentoEditor } from './paginas/OrcamentoEditor.js';
import { OrcamentoNovo } from './paginas/OrcamentoNovo.js';
import { Orcamentos } from './paginas/Orcamentos.js';
import { Usuarios } from './paginas/Usuarios.js';
import { TrocarSenha } from './paginas/TrocarSenha.js';
import type { ModoGerador } from './estado/modo.js';

/**
 * Rotas — Épico 8.
 *
 * Tudo que não for `/login` passa pelo `RotaProtegida`. As telas do gerador de
 * orçamento (8.3) e da importação (8.4) entram aqui nas próximas stories.
 */
export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      {/*
        Trocar senha fica FORA do `Layout` — Story 1.6.

        No primeiro acesso não pode existir menu para clicar: o `RotaProtegida`
        devolveria a pessoa para cá de qualquer jeito, e "clico e volto" é uma
        interface que parece quebrada. Continua protegida (exige sessão), mas o
        gate da senha pendente abre exceção para este caminho — senão o
        redirecionamento se alimentaria.
      */}
      <Route
        path={CAMINHO_TROCA_SENHA}
        element={
          <RotaProtegida>
            <TrocarSenha />
          </RotaProtegida>
        }
      />

      <Route
        element={
          <RotaProtegida>
            <Layout />
          </RotaProtegida>
        }
      >
        <Route index element={<Navigate to="/orcamentos" replace />} />

        {/*
          Dashboard — Frente 2, Stories 2.1 a 2.3.

          Admin **e** gestor. Restrito porque a tela mostra RT, que é a comissão
          do representante — o mesmo motivo que tirou o RT do PDF no item 01 do
          feedback e `ver_dashboard` do vendedor na Story 2.1.

          A rota-índice continua mandando para `/orcamentos`, e não para cá:
          fazer do dashboard a home exigiria decidir por papel, e o vendedor
          precisa cair em `/orcamentos` de qualquer forma. Fica como escolha do
          cliente, não como efeito colateral desta story.
        */}
        <Route
          path="/dashboard"
          element={
            <RotaProtegida papel={['admin', 'gestor']}>
              <Dashboard />
            </RotaProtegida>
          }
        />
        <Route path="/orcamentos" element={<Orcamentos />} />
        {/* `/novo` antes de `/:id` — senão "novo" seria lido como um id. */}
        <Route path="/orcamentos/novo" element={<OrcamentoNovo />} />
        <Route path="/orcamentos/:id" element={<OrcamentoEditor />} />

        {/*
          Seção Del Credere — Épico 9, Story 9.4.

          Os MESMOS componentes, montados noutro caminho. A exclusividade que
          o cliente pediu é de navegação: um menu próprio, uma lista própria e
          uma criação própria. O editor não é reimplementado — ele descobre o
          modo pela tabela do orçamento que carregou.

          A rota `/del-credere/:id` existe para o item do menu continuar ativo
          enquanto o vendedor edita: sem ela, abrir um orçamento o jogaria de
          volta para a seção comum.
        */}
        <Route path="/del-credere" element={<Orcamentos modo="del_credere" />} />
        <Route path="/del-credere/novo" element={<OrcamentoNovo modo="del_credere" />} />
        <Route path="/del-credere/:id" element={<OrcamentoEditor />} />

        {/*
          Seções Vinílicos e Rodapé Engenharia — S5.

          Mesmo desenho do del credere, e derivadas da MESMA lista
          (`SEGMENTOS_ORCAMENTO`, em `packages/shared`) que o servidor usa para
          validar: escrever as rotas à mão aqui criaria a segunda definição de
          quais seções existem, e a divergência apareceria como um menu que leva
          a uma tela que o backend recusa.

          O que muda em relação ao del credere é só COMO a seção se marca: lá é
          o quadrante do orçamento, aqui é o `quote.segmento`. O editor é o
          mesmo, e descobre a seção pelo dado que carregou.
        */}
        {SEGMENTOS_ORCAMENTO.map((s) => (
          <Fragment key={s.code}>
            <Route path={s.rota} element={<Orcamentos modo={s.code as ModoGerador} />} />
            <Route
              path={`${s.rota}/novo`}
              element={<OrcamentoNovo modo={s.code as ModoGerador} />}
            />
            {/* Como a `/del-credere/:id`: existe para o item do menu continuar
                ativo enquanto o vendedor edita. */}
            <Route path={`${s.rota}/:id`} element={<OrcamentoEditor />} />
          </Fragment>
        ))}
        <Route path="/catalogo" element={<Catalogo />} />
        {/* Trocar a tabela de preço é a operação de maior alcance do sistema:
            admin-only aqui e no servidor. */}
        <Route
          path="/importacoes"
          element={
            <RotaProtegida papel="admin">
              <Importacao />
            </RotaProtegida>
          }
        />
        {/*
          Trilha de atividade — Story L.4. Só admin, como `/importacoes`: a
          capacidade `ver_logs` não é herdada pelo gestor.
        */}
        <Route
          path="/logs"
          element={
            <RotaProtegida papel="admin">
              <Logs />
            </RotaProtegida>
          }
        />
        {/*
          Gestão de usuários — Story 1.5, a que fecha a Frente 1. Admin e
          gestor, como a capacidade `gerenciar_usuarios` da 1.1.
        */}
        <Route
          path="/usuarios"
          element={
            <RotaProtegida papel={['admin', 'gestor']}>
              <Usuarios />
            </RotaProtegida>
          }
        />
        <Route
          path="/sem-permissao"
          element={
            /*
              O texto era "restrita a administradores" e passou a mentir na
              Story 2.3: o dashboard também abre para gestor, então um vendedor
              barrado ali leria um motivo errado. A mensagem não diz mais QUEM
              entra — dizer isso certo exigiria saber de qual rota a pessoa veio,
              e a `Navigate` não carrega essa informação.
            */
            <Vazio
              titulo="Sem permissão"
              descricao="Esta área é restrita ao seu perfil de acesso. Se você precisa entrar, fale com o administrador da sua empresa."
            />
          }
        />
        <Route
          path="*"
          element={
            <Vazio titulo="Página não encontrada" descricao="O endereço acessado não existe." />
          }
        />
      </Route>
    </Routes>
  );
}
